import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LanguageService } from '../language/language.service';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { User } from 'src/app/models/user.model';
import { API_V3_KEY } from '../../config/config';

export class ApiCallBase {
  /** The language variable is declared
   * @param language Variable to save the actual language */
  language: string;
  /** The userId variable is declared
   * @param userId Variable to save user ID */
  userId!: string;
  /** The token variable is declared
   * @param token Variable to save the token */
  token!: string;

  user!: User;

  headers: {
    'Content-Type': string;
    'X-ZUMO-APPLICATION': string;
    Accept: string;
    responseType: string;
  };

  apiV3Key!: string;

  /**
   * Following variables declared
   * @param languageService Service that works with the page language
   * @param http Service that allows http request
   * @param router Variable that manages the routes
   */
  constructor(
    public languageService: LanguageService,
    public http: HttpClient,
    public router: Router,
  ) {
    this.loadStorage();
    this.language = languageService.getLanguage();
    /**
     * An alternative to httpOptions_v3 for streaming requests, as streaming requests do not recognize httpOptions_v3.
     */
    this.headers = {
      'Content-Type': 'application/json',
      'X-ZUMO-APPLICATION': API_V3_KEY,
      Accept: 'application/json',
      responseType: 'text',
    };
  }

  /**
   * Recursively converts keys in an object to camelCase.
   *
   * @param {Object} data - The data object to be processed.
   * @returns {Object} The data object with keys converted to camelCase.
   */
  convertKeysToCamelCase(data: object): any {
    if (typeof data !== 'object' || data === null) {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map((item) => this.convertKeysToCamelCase(item));
    }

    return Object.keys(data).reduce((camelCaseData: Record<string, any>, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, group) =>
        group.toUpperCase(),
      );
      camelCaseData[camelKey] = this.convertKeysToCamelCase(
        (data as Record<string, any>)[key],
      );
      return camelCaseData;
    }, {});
  }

  /**
   * Recursively converts keys in an object from camelCase to snake_case.
   *
   * @param {Object} data - The data object to be processed.
   * @returns {Object} The data object with keys converted to snake_case.
   */
  convertCamelToSnakeCase(data: object): any {
    if (typeof data !== 'object' || data === null) {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map((item) => this.convertCamelToSnakeCase(item));
    }

    return Object.keys(data).reduce((snakeCaseData: Record<string, any>, key) => {
      const snakeKey = key.replace(
        /[A-Z]/g,
        (match) => `_${match.toLowerCase()}`,
      );
      snakeCaseData[snakeKey] = this.convertCamelToSnakeCase(
        (data as Record<string, any>)[key],
      );
      return snakeCaseData;
    }, {});
  }

  /**
   * Return HTTP Options for V3 API including user_token key in header.
   */
  getHttpOptionsV3() {
    return {
      headers: new HttpHeaders({
        ...this.headers,
        'X-ZUMO-APPLICATION': API_V3_KEY,
        user_token: localStorage.getItem('token') ?? '',
      }),
    };
  }

  /**
   * Return HTTP Options for V3 API including user_token key in header.
   */
  getHttpOptionsWithoutTokenV3() {
    return {
      headers: new HttpHeaders({
        ...this.headers,
        'X-ZUMO-APPLICATION': API_V3_KEY,
      }),
    };
  }

  /**
   * Return HTTP Options for V3 API including user_token key in header sent as argument.
   */
  getHttpOptionsV3WithToken(token: string) {
    return {
      headers: new HttpHeaders({
        ...this.headers,
        'X-ZUMO-APPLICATION': API_V3_KEY,
        user_token: token,
      }),
    };
  }

  getStreamingHeaders() {
    return {
      ...this.headers,
      user_token: this.token,
    };
  }

  // All header logic for LTI calls will be here to encapsulate it from normal Portal behavior
  getHeadersForLti() {
    const ltiJwt = localStorage.getItem('lti-jwt');

    const ltiHeaders = new HttpHeaders({
      Authorization: `Bearer ${ltiJwt}`,
      'X-ZUMO-APPLICATION': API_V3_KEY,
      'Content-Type': 'application/json',
      Accept: 'application/json',
      responseType: 'text',
    });

    return ltiHeaders;
  }

  getHeadersForLtiMultipart() {
    const ltiJwt = localStorage.getItem('lti-jwt');

    const ltiHeaders = new HttpHeaders({
      Authorization: `Bearer ${ltiJwt}`,
      'X-ZUMO-APPLICATION': API_V3_KEY,
    });

    return ltiHeaders;
  }

  loadStorage() {
    const token = localStorage.getItem('token');
    if (token) {
      this.token = token;
      this.userId = localStorage.getItem('userId') ?? '';
      this.language = localStorage.getItem('language') ?? '';
      this.user = JSON.parse(localStorage.getItem('user') ?? 'null');
    } else {
      this.token = '';
      this.userId = '';
    }
  }

  /**
   * Fetches a response from the given URL and returns an Observable
   * emitting the complete response as a Blob object.
   * @param url - The URL of the resource to fetch.
   * @returns {Observable<any>} An observable that emits the blob response.
   */
  getBlobResponse(url: string): Observable<any> {
    return this.http.get(url, {
      headers: { ...this.headers, user_token: this.token },
      responseType: 'blob',
    });
  }
}
